import React from "react";
import { useState } from "react";
import { GoldSilver } from "./GoldSilver";
import { Vote, VoteJSON } from "../models/vote";
import { Entry } from "../models/entries";
import { CompoCategoriesClient } from "../../../src/entity/CompoCategoriesClient";
import { JudgeOneSong } from "../Vote/VoteWidget";
import { Compo } from "../models/compos";
import { PlaySong } from "../Toolbar/PlaySong";
import { User } from "../models/users";
import { JudgingProgress } from "../Compo/JudgePage";
import Tooltip from "./Tooltip";
import { TestIds } from "../testIds";

interface JudgeListCardProps {
  entry: Entry;
  reviewBounty: number;
  vote: Vote | undefined;
  user: User;
  categories: CompoCategoriesClient[];
  compo: Compo;
  onVote: (vote: VoteJSON) => void;
}

const JudgeListCard = ({
  entry,
  reviewBounty,
  vote,
  categories,
  compo,
  user,
  onVote,
}: JudgeListCardProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-800" data-testid={TestIds.JudgeListCard}>
      <div
        onClick={() => setExpanded(!expanded)}
        className={`flex w-full cursor-pointer items-center gap-3 p-4 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50 ${expanded ? "border-b border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-slate-700/50" : ""}`}
      >
        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {entry.type === "song" ? (
            <PlaySong entry={entry} size="medium" />
          ) : (
            <i className="fa fa-file-o text-xl text-slate-400"></i>
          )}
        </div>
        {vote && (
          <i className="fa fa-check text-emerald-500" data-testid={TestIds.JudgeListCardCheckmark}></i>
        )}
        <span className="text-lg font-medium text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white" data-testid={TestIds.JudgeListCardTitle}>
          {entry.title}
        </span>

        <span className="ml-auto flex-shrink-0">
          <Tooltip
            element={<GoldSilver coins={reviewBounty} />}
            tooltip={
              <div>
                Reviewing this song will earn <GoldSilver coins={reviewBounty} />.
              </div>
            }
          />
        </span>
      </div>

      {expanded && (
        <div className="p-4" onClick={(e) => e.stopPropagation()}>
          <JudgeOneSong
            entry={entry}
            compo={compo}
            vote={vote}
            user={user}
            bounty={reviewBounty}
            categories={categories}
            onVote={(newVote) => {
              setExpanded(false);
              onVote(newVote);
            }}
          />
        </div>
      )}
    </div>
  );
};

interface JudgeListProps {
  judgingProgress: JudgingProgress;
  user: User;
  categories: CompoCategoriesClient[];
  compo: Compo;
  onVote?: (vote: VoteJSON) => void;
}

export const JudgeList = ({
  judgingProgress,
  user,
  categories,
  compo,
  onVote,
}: JudgeListProps): JSX.Element => {
  const requiredEntries = judgingProgress.filter((p) => p.voteRequired);
  const optionalEntries = judgingProgress.filter((p) => !p.voteRequired);

  const renderEntries = (entries: JudgingProgress) =>
    entries.map(({ entry, vote, reviewBounty }) => (
      <JudgeListCard
        key={entry.id}
        reviewBounty={reviewBounty}
        entry={new Entry(entry)}
        vote={vote ? new Vote(vote) : undefined}
        categories={categories}
        compo={compo}
        user={user}
        onVote={(newVote) => {
          if (onVote) {
            onVote(newVote);
          }
        }}
      />
    ));

  return (
    <div>
      {requiredEntries.length > 0 && (
        <div className="mb-6">
          <h2 className="mb-3 text-lg font-semibold text-slate-700 dark:text-slate-300">
            Required Votes
          </h2>
          {renderEntries(requiredEntries)}
        </div>
      )}
      {optionalEntries.length > 0 && (
        <div>
          <h2 className="mb-3 text-lg font-semibold text-slate-700 dark:text-slate-300">
            Optional Votes
          </h2>
          {renderEntries(optionalEntries)}
        </div>
      )}
    </div>
  );
};